/**
 * Pyramid Component (SVG)
 *
 * Renders a square-based pyramid using accurate SVG isometric projection.
 * Matches the style and accuracy of the other 3D components.
 */

import React from 'react';
import { Box } from '@mui/material';

interface PyramidProps {
  base?: number;
  height?: number;
  slantHeight?: number;
  unit?: string;
  color?: string;
  strokeColor?: string;
}

export const Pyramid: React.FC<PyramidProps> = ({
  base = 4,
  height = 5,
  slantHeight,
  unit = 'cm',
  color = '#F0E68C',
  strokeColor = '#9c27b0'
}) => {
  // Center the pyramid in the viewBox
  const centerX = 50;
  const centerY = 60; // Slightly lower to account for apex height
  
  // Normalized sizes for visual consistency
  const maxDim = Math.max(base, height, slantHeight || height);
  const scale = 30 / maxDim; // Base scale factor
  
  const B = base * scale;
  const H = height * scale;
  
  // Isometric projection math
  const cos30 = 0.8660254;
  const sin30 = 0.5;
  
  const project = (x: number, y: number, z: number) => ({
    x: centerX + (x - y) * cos30,
    y: centerY + (x + y) * sin30 - z
  });
  
  // Calculate vertices: square base centered at origin, apex at (0, 0, H)
  // Base corners from -B/2 to B/2
  const pBack = project(-B/2, -B/2, 0);   // Back
  const pRight = project(B/2, -B/2, 0);   // Right
  const pLeft = project(-B/2, B/2, 0);    // Left
  const pFront = project(B/2, B/2, 0);    // Front
  const pApex = project(0, 0, H);         // Apex
  const pCenter = project(0, 0, 0);       // Base center (for height line)
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        {/* Hidden edges (dashed) */}
        <line x1={pBack.x} y1={pBack.y} x2={pRight.x} y2={pRight.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        <line x1={pBack.x} y1={pBack.y} x2={pLeft.x} y2={pLeft.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        <line x1={pApex.x} y1={pApex.y} x2={pBack.x} y2={pBack.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        
        {/* Internal height line (dashed) if height is provided directly */}
        {height !== undefined && slantHeight === undefined && (
          <line x1={pCenter.x} y1={pCenter.y} x2={pApex.x} y2={pApex.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="1,2" opacity={0.6} />
        )}

        {/* Visible Faces with shading */}
        {/* Left face */}
        <polygon
          points={`${pLeft.x},${pLeft.y} ${pFront.x},${pFront.y} ${pApex.x},${pApex.y}`}
          fill={color}
          opacity={0.7}
        />
        {/* Right face */}
        <polygon
          points={`${pRight.x},${pRight.y} ${pFront.x},${pFront.y} ${pApex.x},${pApex.y}`}
          fill={color}
          opacity={0.9}
        />

        {/* Visible Edges */}
        <polygon
          points={`${pLeft.x},${pLeft.y} ${pFront.x},${pFront.y} ${pRight.x},${pRight.y} ${pApex.x},${pApex.y}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        {/* Center edge */}
        <line x1={pApex.x} y1={pApex.y} x2={pFront.x} y2={pFront.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        
        {/* Corner points */}
        <circle cx={pLeft.x} cy={pLeft.y} r={1.2} fill={strokeColor} />
        <circle cx={pFront.x} cy={pFront.y} r={1.2} fill={strokeColor} />
        <circle cx={pRight.x} cy={pRight.y} r={1.2} fill={strokeColor} />
        <circle cx={pApex.x} cy={pApex.y} r={1.2} fill={strokeColor} />
        
        {/* Dimension labels */}
        {/* base */}
        <text
          x={(pLeft.x + pFront.x) / 2 - 4}
          y={(pLeft.y + pFront.y) / 2 + 6}
          fontSize={3.5}
          fill="#374151"
          textAnchor="end"
          fontWeight={600}
        >
          base = {base} {unit}
        </text>
        
        {/* height or slantHeight */}
        {height !== undefined && (
          <text
            x={pApex.x - 3}
            y={(pApex.y + pCenter.y) / 2}
            fontSize={3.5}
            fill="#374151"
            textAnchor="end"
            fontWeight={600}
          >
            h = {height} {unit}
          </text>
        )}
        {slantHeight !== undefined && height === undefined && (
          <text
            x={(pApex.x + pRight.x) / 2 + 3}
            y={(pApex.y + pRight.y) / 2}
            fontSize={3.5}
            fill="#374151"
            textAnchor="start"
            fontWeight={600}
          >
            l = {slantHeight} {unit}
          </text>
        )}
      </svg>
    </Box>
  );
};

export default Pyramid;
